import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  MONTHS,
  RANGE_PRESETS,
  addMonths,
  fmtDate,
  getRange,
  ldnCompose,
  londonDateStr,
  monthGrid,
  rangeLabel,
  type DateRange,
  type RangePreset,
} from '@foodstyles/shared';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../lib/cn';

export interface DateRangeState {
  preset: RangePreset;
  setPreset: (p: RangePreset) => void;
  offset: number;
  setOffset: (n: number) => void;
  customFrom: string;
  customTo: string;
  setCustomFrom: (s: string) => void;
  setCustomTo: (s: string) => void;
  range: DateRange;
  label: string;
}

export function useDateRange(initial: RangePreset = 'week'): DateRangeState {
  const [preset, setPresetRaw] = useState<RangePreset>(initial);
  const [offset, setOffset] = useState(0);
  const today = londonDateStr(0);
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);

  const setPreset = (p: RangePreset) => {
    setPresetRaw(p);
    setOffset(0);
  };
  const range = getRange(preset, Date.now(), customFrom, customTo, offset);
  const label = rangeLabel(preset, offset, range);
  return { preset, setPreset, offset, setOffset, customFrom, customTo, setCustomFrom, setCustomTo, range, label };
}

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const fmtKey = (key: string): string => fmtDate(ldnCompose(key, '12:00'));

function NavBtn({ onClick, label, children }: { onClick: () => void; label: string; children: ReactNode }) {
  return (
    <button onClick={onClick} aria-label={label} className="rounded p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700">
      {children}
    </button>
  );
}

/** One Monday-start month grid with range highlighting. */
function MonthView({
  year,
  month,
  today,
  lo,
  hi,
  onPick,
  onHover,
  className,
}: {
  year: number;
  month: number;
  today: string;
  lo: string | null;
  hi: string | null;
  onPick: (key: string) => void;
  onHover: (key: string | null) => void;
  className?: string;
}) {
  const cells = useMemo(() => monthGrid(year, month), [year, month]);
  return (
    <div className={cn('w-52', className)}>
      <div className="mb-1 text-center text-sm font-semibold text-slate-700 dark:text-slate-200">
        {MONTHS[month - 1]} {year}
      </div>
      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map((d) => (
          <span key={d} className="py-1 text-center text-[11px] font-semibold text-slate-400">
            {d}
          </span>
        ))}
        {cells.map((c) => {
          const inR = !!(lo && hi && c.key >= lo && c.key <= hi);
          const isLo = c.key === lo;
          const isHi = c.key === hi;
          const edge = isLo || isHi;
          const single = isLo && isHi;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onPick(c.key)}
              onMouseEnter={() => onHover(c.key)}
              className="relative h-9 w-full focus:outline-none focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-[#FF5A3C]/40"
              aria-label={fmtKey(c.key)}
            >
              {inR && !single && (
                <span
                  className={cn(
                    'absolute inset-y-1 bg-[#FFE6DF] dark:bg-[#FF5A3C]/20',
                    isLo ? 'left-1/2 right-0' : isHi ? 'left-0 right-1/2' : 'inset-x-0',
                  )}
                />
              )}
              <span
                className={cn(
                  'absolute inset-y-1 left-1/2 z-10 flex w-7 -translate-x-1/2 items-center justify-center rounded-full text-sm',
                  edge
                    ? 'bg-[#FF5A3C] font-semibold text-white'
                    : c.inMonth
                      ? cn('text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700', c.key === today && 'font-bold text-[#D8341A] dark:text-[#FF8A6B]')
                      : 'text-slate-300 dark:text-slate-600',
                )}
              >
                {c.day}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RangePicker({
  state,
  exclude = [],
  align = 'left',
}: {
  state: DateRangeState;
  exclude?: RangePreset[];
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState({ year: 2000, month: 1 });
  const [pendingFrom, setPendingFrom] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const presets = RANGE_PRESETS.filter(([id]) => !exclude.includes(id));

  const today = londonDateStr(0);
  const [todayYear, todayMonth] = today.split('-').map(Number);

  // The currently-applied custom range, as London date keys. Derived from
  // state.range so it stays correct when the ‹ › steppers shift it by `offset`.
  const appliedFrom = state.preset === 'custom' ? londonDateStr(0, state.range.from) : null;
  const appliedTo = state.preset === 'custom' ? londonDateStr(0, state.range.to - 1) : null;

  // Move focus into the popover on open; Escape closes and restores the trigger.
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const openPopover = () => {
    const base = appliedFrom ?? today;
    const [y, m] = base.split('-').map(Number);
    setView({ year: y, month: m });
    setPendingFrom(null);
    setHover(null);
    setOpen(true);
  };

  const choosePreset = (id: RangePreset) => {
    state.setPreset(id);
    setOpen(false);
  };

  const pickDay = (key: string) => {
    if (!pendingFrom) {
      setPendingFrom(key);
      return;
    }
    const [from, to] = key < pendingFrom ? [key, pendingFrom] : [pendingFrom, key];
    state.setCustomFrom(from);
    state.setCustomTo(to);
    state.setPreset('custom'); // applies (and resets offset) only now that both ends are chosen
    setPendingFrom(null);
    setHover(null);
    setOpen(false);
  };

  // Highlight: the in-progress pick (with live hover preview) takes precedence
  // over the currently-applied custom range.
  let lo: string | null = null;
  let hi: string | null = null;
  if (pendingFrom) {
    const other = hover ?? pendingFrom;
    [lo, hi] = pendingFrom < other ? [pendingFrom, other] : [other, pendingFrom];
  } else {
    lo = appliedFrom;
    hi = appliedTo;
  }

  const right = addMonths(view.year, view.month, 1);
  const footer = pendingFrom
    ? `${fmtKey(pendingFrom)} → pick an end date`
    : appliedFrom && appliedTo
      ? `${fmtKey(appliedFrom)} – ${fmtKey(appliedTo)}`
      : 'Click a start date, then an end date';

  return (
    <div className="relative inline-flex items-stretch" ref={ref}>
      <button
        ref={triggerRef}
        onClick={() => (open ? setOpen(false) : openPopover())}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-l-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
      >
        <Calendar size={15} className="text-slate-400" />
        {state.label}
      </button>
      <button
        onClick={() => state.setOffset(state.offset - 1)}
        aria-label="Previous period"
        className="border-y border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
      >
        <ChevronLeft size={16} />
      </button>
      <button
        onClick={() => state.setOffset(state.offset + 1)}
        disabled={state.offset >= 0 && state.preset !== 'custom'}
        aria-label="Next period"
        className="rounded-r-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40"
      >
        <ChevronRight size={16} />
      </button>

      {open && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-label="Choose date range"
          tabIndex={-1}
          className={cn(
            'absolute top-full z-40 mt-1 flex max-w-[95vw] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 shadow-lg focus:outline-none',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          <div className="w-32 shrink-0 space-y-0.5 border-r border-slate-100 dark:border-slate-700 pr-2">
            {presets.map(([id, label]) => (
              <button
                key={id}
                onClick={() => choosePreset(id)}
                className={cn(
                  'block w-full rounded-md px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700',
                  state.preset === id ? 'font-semibold text-[#D8341A] dark:text-[#FF8A6B]' : 'text-slate-600 dark:text-slate-300',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="pl-3" onMouseLeave={() => setHover(null)}>
            <div className="mb-1 flex items-center justify-between">
              <div className="flex gap-0.5">
                <NavBtn onClick={() => setView(addMonths(view.year, view.month, -12))} label="Previous year">
                  <ChevronsLeft size={16} />
                </NavBtn>
                <NavBtn onClick={() => setView(addMonths(view.year, view.month, -1))} label="Previous month">
                  <ChevronLeft size={16} />
                </NavBtn>
              </div>
              <div className="flex gap-0.5">
                <NavBtn onClick={() => setView(addMonths(view.year, view.month, 1))} label="Next month">
                  <ChevronRight size={16} />
                </NavBtn>
                <NavBtn onClick={() => setView(addMonths(view.year, view.month, 12))} label="Next year">
                  <ChevronsRight size={16} />
                </NavBtn>
              </div>
            </div>
            <div className="flex gap-4">
              <MonthView
                year={view.year}
                month={view.month}
                today={today}
                lo={lo}
                hi={hi}
                onPick={pickDay}
                onHover={setHover}
              />
              <MonthView
                className="hidden sm:block"
                year={right.year}
                month={right.month}
                today={today}
                lo={lo}
                hi={hi}
                onPick={pickDay}
                onHover={setHover}
              />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-100 dark:border-slate-700 px-1 pt-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">{footer}</span>
              <button
                onClick={() => setView({ year: todayYear, month: todayMonth })}
                className="shrink-0 rounded px-2 py-0.5 text-xs font-semibold text-[#D8341A] dark:text-[#FF8A6B] hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Today
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
